<script setup>
// NOTICE: KAPTION-INTEGRATION
import { computed, onMounted, onUnmounted, ref, watch, toRef } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { useMessageContext } from '../provider.js';
import { MESSAGE_VARIANTS } from '../constants';
import { useUserLanguage } from 'dashboard/composables/useUserLanguage';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
// NOTICE: KAPTION-INTEGRATION - Use composable for consistent preferences + master toggle check
import { useKaptionAIFeaturesPreferences } from 'dashboard/composables/useKaptionAIFeaturesPreferences';
import { useUISettings } from 'dashboard/composables/useUISettings';
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';

const props = defineProps({
  contentAttributes: {
    type: Object,
    required: true,
  },
  originalContent: {
    type: String,
    required: true,
  },
  conversationId: {
    type: Number,
    required: true,
  },
  // NOTICE: KAPTION-INTEGRATION
  isMessageTranslating: {
    type: Boolean,
    default: false,
  },
  messageId: {
    type: Number,
    required: true,
  },
});

// NOTICE: KAPTION-INTEGRATION - Watch contentAttributes deeply to catch ActionCable updates
watch(
  () => props.contentAttributes,
  () => {
    // Content attributes changed
  },
  { deep: true }
);

const { variant } = useMessageContext();
const { userLanguage } = useUserLanguage();
const { currentAccount } = useAccount();
const currentChat = useMapGetter('getSelectedChat');

// NOTICE: KAPTION-INTEGRATION
// Get the message's source language from the translation metadata
const messageSourceLanguage = computed(() => {
  const contentAttrs = props.contentAttributes;
  if (!contentAttrs) return null;

  // NOTICE: Check old format FIRST - it often has more reliable source_language
  // (especially for older messages that were translated before the new format)
  const oldTranslation = contentAttrs.kaptionTranslation;
  if (oldTranslation) {
    // NOTICE: KAPTION-INTEGRATION - Check camelCase first after deep conversion
    const sourceLang =
      oldTranslation.sourceLanguage || oldTranslation.source_language;
    if (sourceLang && sourceLang !== 'unknown' && sourceLang !== 'Unknown') {
      return sourceLang;
    }
  }

  // Check new format (multiple translations)
  const translations = contentAttrs.kaptionTranslations;
  if (translations && typeof translations === 'object') {
    // Get source_language from ANY translation
    // NOTICE: Prioritize translations with valid source_language (not "unknown")
    // Some old translations might have "unknown" which is not useful
    let validSourceLang = null;
    let fallbackSourceLang = null;

    const translationKeys = Object.keys(translations);
    for (let i = 0; i < translationKeys.length; i += 1) {
      const lang = translationKeys[i];
      const translation = translations[lang];
      // NOTICE: KAPTION-INTEGRATION - After useCamelCase conversion, check camelCase FIRST
      // The MessageList.vue converts with { deep: true }, so nested keys are also camelCase
      const sourceLang =
        translation?.sourceLanguage || translation?.source_language;

      if (sourceLang) {
        // Prefer valid language codes over "unknown"
        if (sourceLang !== 'unknown' && sourceLang !== 'Unknown') {
          validSourceLang = sourceLang;
          break;
        } else {
          // Keep "unknown" as fallback if nothing better found
          fallbackSourceLang = sourceLang;
        }
      }
    }

    // Return valid source language if found, otherwise fallback
    if (validSourceLang) {
      return validSourceLang;
    }
    if (fallbackSourceLang) {
      return fallbackSourceLang;
    }
  }

  return null;
});

// NOTICE: KAPTION-INTEGRATION
// Get translation for user's language preference
// Priority: userLanguage → account locale → don't show wrong translation, queue the correct one
const kaptionTranslation = computed(() => {
  const contentAttrs = props.contentAttributes;
  if (!contentAttrs) return null;

  // NOTICE: KAPTION-INTEGRATION - Explicitly access nested properties to ensure Vue tracks reactivity
  // Accessing these forces Vue to watch for changes via message.updated ActionCable events
  const multiLangTranslations = contentAttrs?.kaptionTranslations;
  const singleTranslation = contentAttrs?.kaptionTranslation;

  const userLang = userLanguage?.value;
  const sourceLang = messageSourceLanguage.value;

  // NOTICE: KAPTION-INTEGRATION - If message is already in user's language, don't show translation
  if (sourceLang && userLang && sourceLang === userLang) {
    return null;
  }

  // Check new format first (multiple translations)
  if (multiLangTranslations && typeof multiLangTranslations === 'object') {
    // Try user's language first
    if (userLang && multiLangTranslations[userLang]) {
      return multiLangTranslations[userLang];
    }

    // Fallback to account locale
    const locale = currentAccount?.value?.locale;
    if (locale && typeof locale === 'string') {
      const accountLocale = locale.split('_')[0]?.toLowerCase?.();
      if (accountLocale && multiLangTranslations[accountLocale]) {
        return multiLangTranslations[accountLocale];
      }
    }

    // NOTICE: KAPTION-INTEGRATION - Don't show wrong translation
    // User's language translation doesn't exist, return null
    // MessagesView will detect this and trigger auto-translation
    // Component will show original + loading skeleton via isTranslating prop

    // Return null - no translation to show yet
    return null;
  }

  // Fallback to old format (single translation) for backwards compatibility
  return singleTranslation || null;
});

const hasKaptionTranslation = computed(() => {
  return kaptionTranslation.value?.text;
});

// NOTICE: KAPTION-INTEGRATION - Use composable for per-conversation preferences
// This ensures consistent localStorage keys across all components
const conversationIdRef = toRef(props, 'conversationId');
const { translationEnabled, setTranslation } =
  useKaptionAIFeaturesPreferences(conversationIdRef);

// NOTICE: KAPTION-INTEGRATION - Get master toggle from uiSettings (topbar setting)
const { uiSettings } = useUISettings();
const masterToggleEnabled = computed(
  () => uiSettings.value?.kaption_live_translation_enabled ?? true
);

// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission gate
// feature-permissions.AI.2 - hide translated content when permission is revoked
const { canUseFeature } = useFeaturePermissions();

// NOTICE: KAPTION-INTEGRATION - Combined check: master toggle AND per-conversation preference
// This ensures translations are hidden when the global toggle is OFF
const isTranslationEnabled = computed(() => {
  return (
    masterToggleEnabled.value &&
    translationEnabled.value &&
    canUseFeature('live_translation')
  );
});

// NOTICE: KAPTION-INTEGRATION - Format translated text with markdown rendering
const formattedTranslation = computed(() => {
  if (!kaptionTranslation.value?.text) return '';

  // Use same formatting logic as FormattedContent
  if (variant.value === MESSAGE_VARIANTS.ACTIVITY) {
    return kaptionTranslation.value.text;
  }

  return new MessageFormatter(kaptionTranslation.value.text).formattedMessage;
});

// NOTICE: KAPTION-INTEGRATION - Format original content with markdown rendering
const formattedOriginal = computed(() => {
  if (!props.originalContent) return '';

  // Use same formatting logic as FormattedContent
  if (variant.value === MESSAGE_VARIANTS.ACTIVITY) {
    return props.originalContent;
  }

  return new MessageFormatter(props.originalContent).formattedMessage;
});

// NOTICE: KAPTION-INTEGRATION
// Get conversation language from conversation metadata
const conversationLanguage = computed(() => {
  const chat = currentChat.value;
  if (!chat?.additionalAttributes) return null;

  // Check for conversation_language in additional_attributes
  const lang =
    chat.additionalAttributes.conversationLanguage ||
    chat.additionalAttributes.conversation_language;
  return lang || null;
});

// NOTICE: KAPTION-INTEGRATION
// Check if conversation language matches user language (no translation needed)
const shouldSkipTranslation = computed(() => {
  const convLang = conversationLanguage.value;
  const userLang = userLanguage.value;

  // If conversation language matches user language, skip translation
  if (
    convLang &&
    userLang &&
    convLang.toLowerCase() === userLang.toLowerCase()
  ) {
    return true;
  }

  return false;
});

// NOTICE: KAPTION-INTEGRATION
// Track when translation completes to remove loading state
const isTranslating = ref(false);
const translationTimeoutId = ref(null);

// NOTICE: KAPTION-INTEGRATION - Initial check for existing translations
// Don't show translating state if translation already exists on mount
if (kaptionTranslation.value?.text || shouldSkipTranslation.value) {
  isTranslating.value = false;
} else if (props.isMessageTranslating) {
  isTranslating.value = true;
  // Only set timeout if we're actually translating
  translationTimeoutId.value = setTimeout(() => {
    isTranslating.value = false;
    translationTimeoutId.value = null;
  }, 3000);
}

// NOTICE: KAPTION-INTEGRATION - Watch for translation data arrival
// When translation actually appears, clear loading state automatically
watch(
  () => kaptionTranslation.value?.text,
  newText => {
    if (newText) {
      isTranslating.value = false;
      if (translationTimeoutId.value) {
        clearTimeout(translationTimeoutId.value);
        translationTimeoutId.value = null;
      }
    }
  }
);

// NOTICE: KAPTION-INTEGRATION
// Watch for isMessageTranslating prop changes
watch(
  () => props.isMessageTranslating,
  newValue => {
    // Don't show processing if conversation language matches user language
    if (shouldSkipTranslation.value) {
      isTranslating.value = false;
      return;
    }

    // NOTICE: KAPTION-INTEGRATION - Don't show translating if translation already exists
    // This prevents the 3-second delay when messages are already translated
    if (kaptionTranslation.value?.text) {
      isTranslating.value = false;
      return;
    }

    if (newValue) {
      isTranslating.value = true;

      // Set timeout to hide processing message after 3 seconds
      if (translationTimeoutId.value) {
        clearTimeout(translationTimeoutId.value);
      }

      translationTimeoutId.value = setTimeout(() => {
        isTranslating.value = false;
        translationTimeoutId.value = null;
      }, 3000);
    } else {
      isTranslating.value = false;
      if (translationTimeoutId.value) {
        clearTimeout(translationTimeoutId.value);
        translationTimeoutId.value = null;
      }
    }
  }
);

// NOTICE: KAPTION-INTEGRATION - Listen for translation toggle events from KaptionAIFeaturesPill
// Update the composable's ref when the event fires to maintain reactivity
const handleTranslationToggle = ({ conversationId, enabled }) => {
  if (conversationId === props.conversationId) {
    setTranslation(enabled);
  }
};

// NOTICE: KAPTION-INTEGRATION
// Listen for translation completion events (from emitter)
const handleTranslationCompleted = data => {
  if (data.message_id === props.messageId) {
    isTranslating.value = false;
    if (translationTimeoutId.value) {
      clearTimeout(translationTimeoutId.value);
      translationTimeoutId.value = null;
    }
  }
};

onMounted(() => {
  emitter.on('kaption-translation-toggled', handleTranslationToggle);
  emitter.on('kaption-translation-completed', handleTranslationCompleted);
});

onUnmounted(() => {
  emitter.off('kaption-translation-toggled', handleTranslationToggle);
  emitter.off('kaption-translation-completed', handleTranslationCompleted);

  // Clean up timeout if component unmounts while translating
  if (translationTimeoutId.value) {
    clearTimeout(translationTimeoutId.value);
    translationTimeoutId.value = null;
  }
});
</script>

<template>
  <!-- NOTICE: KAPTION-INTEGRATION - Translation loading state with skeleton -->
  <!-- Show original message + loading skeleton while waiting for translation -->
  <div v-if="isTranslating" class="kaption-translation-loading">
    <!-- Original message at normal size -->
    <span
      v-dompurify-html="formattedOriginal"
      class="text-base prose prose-bubble mb-1 opacity-100"
    />
    <!-- Processing indicator text -->
    <div class="text-xs text-n-slate-11 mt-1 flex items-center gap-1">
      <span
        class="inline-block w-2 h-2 bg-n-brand rounded-full animate-pulse"
      />
      {{ $t('TRANSLATION.PROCESSING') }}
    </div>
  </div>

  <!-- When translations are enabled, show translation with original -->
  <div
    v-else-if="hasKaptionTranslation && isTranslationEnabled"
    class="kaption-translation-display"
  >
    <!-- Original message (very small, at top) - rendered as markdown -->
    <!-- NOTICE: KAPTION-INTEGRATION - Use prose-sm and explicit text-[10px] for nested content -->
    <span
      v-dompurify-html="formattedOriginal"
      class="block !text-[10px] opacity-60 mb-1 leading-tight prose prose-sm prose-bubble [&_p]:!text-[10px]"
    />

    <!-- Translated message (normal size, below) - rendered as markdown -->
    <span
      v-dompurify-html="formattedTranslation"
      class="text-base prose prose-bubble"
    />
  </div>

  <!-- When translations are disabled, show only original content - rendered as markdown -->
  <div
    v-else-if="hasKaptionTranslation && !isTranslationEnabled"
    class="kaption-original-display"
  >
    <span
      v-dompurify-html="formattedOriginal"
      class="text-base prose prose-bubble"
    />
  </div>

  <!-- NOTICE: KAPTION-INTEGRATION - Always show original message if no translation or error -->
  <!-- This ensures messages never disappear even if translation fails -->
  <div v-else class="kaption-fallback-display">
    <span
      v-dompurify-html="formattedOriginal"
      class="text-base prose prose-bubble"
    />
  </div>
</template>
